import React, { useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import Skeleton from "@mui/material/Skeleton";
import Price from "./Price";
import UpdatePriceModal from "@/modals/updatePriceModal";
import { EditOutlined } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import PreLoader from "@/components/preLoader";
import { groupBy } from "lodash";

import { useLazyQuery } from "@apollo/client";
import { PRODUCT_SEARCH } from "@/services/productService";

import { Colors } from "@/theme";
import { getStoreAddressString } from "@/utils/otherUtil";
import {
  RANGES_FOR_PRODUCT_SEARCH,
  FUEL_PRODUCT_CODES,
  KM_IN_MILE,
  STORE_INTEGRATION_TYPE,
  PRICE_HISTORY_LIMIT,
} from "@/constants/config";
import { MERCHANT_ID } from "@/constants/env";
import useStyles from "./styles";
import moment from "moment";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 18,
    backgroundColor: Colors.WHITE,
    color: Colors.DIM_GREY,
    borderBottomColor: Colors.GRAY_BORDER,
  },
}));

const TableRowComponent = ({
  localization,
  loading,
  stores,
  onPriceUpdateSuccess,
}) => {
  const classes = useStyles();

  const [openUpdatePrice, setOpenUpdatePrice] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  const productsGroupByStore = useRef([]);

  const [productSearchQuery, { loading: isLoadingProductSearch }] =
    useLazyQuery(PRODUCT_SEARCH, {
      context: {
        headers: {
          merchantId: MERCHANT_ID,
        },
      },
      fetchPolicy: "no-cache",
      onCompleted: (response) => {
        const responseWithMileDistance = response.product_search?.map(
          (item) => ({
            ...item,
            distance: item.distance / KM_IN_MILE,
          })
        );
        const data = groupBy(
          responseWithMileDistance,
          (product) => product.store.id
        );
        productsGroupByStore.current = Object.keys(data)
          .filter((storeId) => storeId !== selectedStore.id)
          .map((storeId) => data[storeId]);

        setOpenUpdatePrice(true);
      },
    });

  const onEditPrice = (store) => {
    setSelectedStore(store);
    productSearchQuery({
      variables: {
        product_code: FUEL_PRODUCT_CODES,
        geo: {
          location: {
            latitude: store.address?.location?.latitude,
            longitude: store.address?.location?.longitude,
          },
          range: RANGES_FOR_PRODUCT_SEARCH[2] * KM_IN_MILE,
        },
        limit: PRICE_HISTORY_LIMIT,
      },
    });
  };

  const onCloseUpdatePrice = () => {
    setOpenUpdatePrice(false);
  };

  if (loading) {
    return (
      <TableBody>
        {Array.apply(null, Array(3))
          .map(function (y, i) {
            return i;
          })
          .map((id) => (
            <TableRow key={`skeleton-${id}`} sx={{ height: 130 }}>
              <StyledTableCell>
                <Skeleton
                  variant="circular"
                  width={70}
                  height={70}
                  sx={{ margin: "auto" }}
                />
              </StyledTableCell>
              <StyledTableCell align="left">
                <Skeleton variant="rounded" width={"100%"} height={50} />
              </StyledTableCell>
              <StyledTableCell align="left">
                <Skeleton variant="rounded" width={"100%"} height={50} />
              </StyledTableCell>
              <StyledTableCell align="left">
                <Skeleton variant="rounded" width={"100%"} height={50} />
              </StyledTableCell>
              <StyledTableCell align="left">
                <Skeleton variant="rounded" width={"100%"} height={50} />
              </StyledTableCell>
            </TableRow>
          ))}
      </TableBody>
    );
  }

  return (
    <TableBody>
      {stores?.map((store, id) => (
        <TableRow key={`store-${id}`} sx={{ height: 130 }}>
          <StyledTableCell align="center">
            <img src={store.brand?.logo?.thumbnail} width={70} height={70} />
          </StyledTableCell>
          <StyledTableCell align="left">
            {
              store.integrations?.find(
                (item) =>
                  item.integration_type === STORE_INTEGRATION_TYPE.LIVE_PRICE
              )?.integration_id
            }
          </StyledTableCell>
          <StyledTableCell align="left">{store.name}</StyledTableCell>
          <StyledTableCell align="left">
            {getStoreAddressString(store.address)}
          </StyledTableCell>
          <StyledTableCell align="left">
            <Box display={"flex"}>
              {store.products?.map((product, pid, array) => (
                <Box key={`${id}-${pid}`}>
                  <Price
                    isLast={pid === array.length - 1}
                    localization={localization}
                    code={product.product_code}
                    price={parseFloat(product?.prices?.[0]?.gross_price)}
                    trend={product.prices?.[0]?.history?.trend}
                  />
                  <span className={classes.dateText}>
                    {moment
                      .utc(product?.prices?.[0]?.last_updated)
                      .local()
                      .format("HH:mm DD/MM/YY")}
                  </span>
                </Box>
              ))}
              <IconButton
                sx={{ padding: 1, mr: 4 }}
                onClick={() => onEditPrice(store)}
              >
                <EditOutlined sx={{ fontSize: 22, color: Colors.DIM_GREY }} />
              </IconButton>
            </Box>
          </StyledTableCell>
        </TableRow>
      ))}

      {openUpdatePrice && selectedStore && (
        <UpdatePriceModal
          open={openUpdatePrice}
          onClose={onCloseUpdatePrice}
          store={selectedStore}
          products={selectedStore.products}
          otherStoresProducts={productsGroupByStore.current}
          onUpdateSuccess={onPriceUpdateSuccess}
        />
      )}

      {isLoadingProductSearch && <PreLoader size={40} />}
    </TableBody>
  );
};

export default TableRowComponent;
